import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listAttachments,
  uploadAttachment,
  deleteAttachment,
  attachmentUrl,
  updateRecord,
  listReports,
  saveReport,
  deleteReport,
} from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { streamAi } from "@/lib/aiStream";
import Markdown from "@/components/Markdown";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import { toast } from "sonner";
import {
  X,
  Image as ImageIcon,
  FileText,
  UploadCloud,
  Trash2,
  Download,
  Loader2,
  Camera,
  FileUp,
  Calendar,
  MapPin,
  User as UserIcon,
  Building2,
  StickyNote,
  Sparkles,
  Copy,
  Check,
  StopCircle,
  History,
  Save,
  Eye,
  ChevronDown,
  ChevronUp,
  FileType2,
} from "lucide-react";

const STATUS_OPTIONS = ["Draft", "Berjalan", "Selesai", "Batal"];

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-700 border-slate-300",
  Berjalan: "bg-amber-50 text-amber-800 border-amber-300",
  Selesai: "bg-emerald-50 text-emerald-800 border-emerald-300",
  Batal: "bg-rose-50 text-rose-800 border-rose-300",
};

export default function SpdDetailModal({ record, onClose }) {
  const qc = useQueryClient();
  const photoRef = useRef(null);
  const docRef = useRef(null);
  const [uploading, setUploading] = useState(null); // 'photo' | 'document' | null
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null); // photo URL
  const [aiReport, setAiReport] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false); // has current report been saved?
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null); // attachment being previewed
  const aiAbortRef = useRef(null);

  const { data: reports = [] } = useQuery({
    queryKey: ["reports", record.id],
    queryFn: () => listReports(record.id),
  });

  const saveReportMut = useMutation({
    mutationFn: () =>
      saveReport(record.id, aiReport, attachments.filter((a) => a.kind === "photo").length),
    onSuccess: () => {
      toast.success("Laporan disimpan ke riwayat");
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["reports", record.id] });
    },
    onError: () => toast.error("Gagal menyimpan laporan"),
  });

  const deleteReportMut = useMutation({
    mutationFn: (reportId) => deleteReport(record.id, reportId),
    onSuccess: () => {
      toast.success("Laporan riwayat dihapus");
      qc.invalidateQueries({ queryKey: ["reports", record.id] });
    },
    onError: () => toast.error("Gagal menghapus"),
  });

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: ["attachments", record.id],
    queryFn: () => listAttachments(record.id),
  });

  const statusMut = useMutation({
    mutationFn: (newStatus) =>
      updateRecord(record.id, { ...record.data, status: newStatus }),
    onSuccess: () => {
      toast.success("Status diperbarui");
      qc.invalidateQueries({ queryKey: ["records", "program"] });
    },
    onError: () => toast.error("Gagal update status"),
  });

  const deleteMut = useMutation({
    mutationFn: (attId) => deleteAttachment(record.id, attId),
    onSuccess: () => {
      toast.success("Lampiran dihapus");
      qc.invalidateQueries({ queryKey: ["attachments", record.id] });
    },
    onError: () => toast.error("Gagal hapus lampiran"),
  });

  const handleUpload = async (file, kindHint) => {
    if (!file) return;
    setUploading(kindHint);
    setProgress(0);
    try {
      let toUpload = file;
      let compressionInfo = null;
      if (kindHint === "photo") {
        const originalSize = file.size;
        toUpload = await compressImage(file);
        if (toUpload.size < originalSize) {
          const savedPct = Math.round((1 - toUpload.size / originalSize) * 100);
          compressionInfo = `hemat ${savedPct}%`;
        }
      }
      await uploadAttachment(record.id, toUpload, setProgress);
      const label = kindHint === "photo" ? "Foto" : "Dokumen";
      toast.success(
        compressionInfo
          ? `${label} diunggah (${compressionInfo})`
          : `${label} berhasil diunggah`
      );
      qc.invalidateQueries({ queryKey: ["attachments", record.id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal upload");
    } finally {
      setUploading(null);
      setProgress(0);
    }
  };

  const photos = attachments.filter((a) => a.kind === "photo");
  const docs = attachments.filter((a) => a.kind === "document");
  const currentStatus = record.data?.status || "Draft";

  const generateReport = async () => {
    if (aiBusy) return;
    if (photos.length === 0) {
      toast.error("Unggah minimal 1 foto dulu untuk generate laporan AI");
      return;
    }
    setAiBusy(true);
    setAiReport("");
    setCopied(false);
    setSaved(false);
    const controller = new AbortController();
    aiAbortRef.current = controller;
    await streamAi(
      `/ai/spd-report/${record.id}`,
      {},
      {
        signal: controller.signal,
        onDelta: (t) => setAiReport((s) => s + t),
        onError: (e) => {
          toast.error(e.message || "Gagal generate laporan");
          setAiReport((s) => s + `\n\n**Error**: ${e.message}`);
        },
        onDone: () => toast.success("Laporan AI selesai — jangan lupa simpan ke riwayat"),
      }
    );
    setAiBusy(false);
  };

  const stopReport = () => {
    if (aiAbortRef.current) aiAbortRef.current.abort();
    setAiBusy(false);
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(aiReport);
      setCopied(true);
      toast.success("Laporan tersalin ke clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const downloadReport = () => {
    const blob = new Blob([aiReport], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safe = (record.data?.nama || "spd").replace(/[^a-z0-9]+/gi, "_");
    a.download = `laporan-spd-${safe}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDocx = async () => {
    try {
      const safe = (record.data?.nama || "spd").replace(/[^a-z0-9]+/gi, "_");
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${BACKEND_URL}/api/export-docx`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: aiReport,
          filename: `laporan-spd-${safe}`,
        }),
      });
      if (!resp.ok) throw new Error("Gagal ekspor docx");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-spd-${safe}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan .docx berhasil diunduh");
    } catch (e) {
      toast.error(e.message || "Gagal unduh .docx");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="spd-detail-modal"
    >
      <div
        className="bg-white border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div className="flex-1 min-w-0">
            <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-1">
              Detail SPD · {record.data?.bulan || "-"}
            </div>
            <h3 className="font-display font-black text-xl tracking-tight">
              {record.data?.nama || "SPD"}
            </h3>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span
                className={`text-[11px] font-semibold px-2 py-1 border ${STATUS_STYLES[currentStatus] || STATUS_STYLES.Draft}`}
              >
                {currentStatus}
              </span>
              <select
                data-testid="status-select"
                value={currentStatus}
                onChange={(e) => statusMut.mutate(e.target.value)}
                disabled={statusMut.isPending}
                className="text-xs border border-slate-300 bg-white px-2 py-1 focus:outline-none focus:border-[#0047FF]"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>Ubah ke: {s}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="detail-close"
            className="p-1.5 hover:bg-slate-100 shrink-0 ml-4"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info grid */}
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
              Informasi Perjalanan
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <InfoRow icon={UserIcon} label="Personel" value={record.data?.nama} />
              <InfoRow icon={Building2} label="Cabang" value={record.data?.cabang} />
              <InfoRow icon={Calendar} label="Tanggal" value={record.data?.tanggal} />
              <InfoRow icon={MapPin} label="Tujuan" value={record.data?.tujuan} />
              <div className="sm:col-span-2">
                <InfoRow icon={StickyNote} label="Keterangan" value={record.data?.keterangan} />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                  Dokumentasi Foto
                </div>
                <h4 className="font-display font-bold text-sm">
                  {photos.length} foto
                </h4>
              </div>
              <button
                data-testid="upload-photo-btn"
                onClick={() => photoRef.current?.click()}
                disabled={uploading}
                className="btn-sharp inline-flex items-center gap-2 bg-[#0047FF] text-white text-xs font-semibold px-3 py-2 hover:bg-[#0033CC] disabled:opacity-50"
              >
                {uploading === "photo" ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {progress}%
                  </>
                ) : (
                  <>
                    <Camera className="w-3.5 h-3.5" /> Unggah Foto
                  </>
                )}
              </button>
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0], "photo")}
                data-testid="photo-input"
              />
            </div>
            {isLoading ? (
              <div className="text-xs text-slate-400 py-6 text-center">Memuat...</div>
            ) : photos.length === 0 ? (
              <div
                onClick={() => photoRef.current?.click()}
                className="border-2 border-dashed border-slate-300 py-8 text-center cursor-pointer hover:border-[#0047FF] hover:bg-slate-50"
              >
                <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-xs text-slate-500">
                  Belum ada foto. Klik untuk unggah.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {photos.map((p) => {
                  const url = attachmentUrl(p.storage_path);
                  return (
                    <div
                      key={p.id}
                      className="relative group aspect-square bg-slate-100 overflow-hidden border border-slate-200"
                    >
                      <img
                        src={url}
                        alt={p.original_name}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setPreview(url)}
                        data-testid={`photo-${p.id}`}
                      />
                      <button
                        data-testid={`delete-photo-${p.id}`}
                        onClick={() => {
                          if (window.confirm("Hapus foto ini?")) deleteMut.mutate(p.id);
                        }}
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-rose-600 text-white p-1 hover:bg-rose-700 transition-opacity"
                        title="Hapus"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="text-[10px] text-white truncate">
                          {p.original_name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                  Laporan Hasil SPD
                </div>
                <h4 className="font-display font-bold text-sm">
                  {docs.length} dokumen
                </h4>
              </div>
              <button
                data-testid="upload-doc-btn"
                onClick={() => docRef.current?.click()}
                disabled={uploading}
                className="btn-sharp inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-semibold px-3 py-2 hover:bg-[#0047FF] disabled:opacity-50"
              >
                {uploading === "document" ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {progress}%
                  </>
                ) : (
                  <>
                    <FileUp className="w-3.5 h-3.5" /> Unggah Dokumen
                  </>
                )}
              </button>
              <input
                ref={docRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0], "document")}
                data-testid="doc-input"
              />
            </div>
            {docs.length === 0 ? (
              <div
                onClick={() => docRef.current?.click()}
                className="border-2 border-dashed border-slate-300 py-6 text-center cursor-pointer hover:border-[#0047FF] hover:bg-slate-50"
              >
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-xs text-slate-500">
                  Belum ada laporan. Klik untuk unggah (PDF/DOC/XLS).
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => {
                  const isPdf =
                    d.original_name?.toLowerCase().endsWith(".pdf") ||
                    d.content_type === "application/pdf";
                  return (
                  <div
                    key={d.id}
                    className="flex items-center gap-3 p-3 border border-slate-200 hover:border-slate-400"
                  >
                    <div className="w-10 h-10 bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {d.original_name}
                      </div>
                      <div className="mono text-[11px] text-slate-500">
                        {(d.size / 1024).toFixed(1)} KB ·{" "}
                        {new Date(d.created_at).toLocaleString("id-ID")}
                      </div>
                    </div>
                    {isPdf && (
                      <button
                        data-testid={`preview-doc-${d.id}`}
                        onClick={() => setPdfPreview(d)}
                        className="p-2 border border-slate-300 hover:border-[#0047FF] hover:text-[#0047FF]"
                        title="Preview inline"
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                      </button>
                    )}
                    <a
                      href={attachmentUrl(d.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`download-doc-${d.id}`}
                      className="p-2 border border-slate-300 hover:border-slate-900"
                      title="Unduh"
                    >
                      <Download className="w-4 h-4 text-slate-600" />
                    </a>
                    <button
                      data-testid={`delete-doc-${d.id}`}
                      onClick={() => {
                        if (window.confirm("Hapus dokumen ini?"))
                          deleteMut.mutate(d.id);
                      }}
                      className="p-2 border border-slate-300 hover:border-rose-500 hover:text-rose-500"
                      title="Hapus"
                    >
                      <Trash2 className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* AI Auto Report */}
          <div className="border-t border-slate-200 pt-6" data-testid="ai-report-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                  AI Auto-Report · Claude Sonnet 4.6 Vision
                </div>
                <h4 className="font-display font-bold text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#0047FF]" />
                  Generate Draft Laporan dari Foto
                </h4>
              </div>
              <div className="flex items-center gap-1">
                {aiReport && !aiBusy && (
                  <>
                    <button
                      data-testid="ai-report-save"
                      onClick={() => saveReportMut.mutate()}
                      disabled={saved || saveReportMut.isPending}
                      className="btn-sharp inline-flex items-center gap-1 text-xs border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1.5 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Simpan ke Riwayat"
                    >
                      {saved ? (
                        <>
                          <Check className="w-3.5 h-3.5" /> Tersimpan
                        </>
                      ) : (
                        <>
                          <Save className="w-3.5 h-3.5" /> Simpan
                        </>
                      )}
                    </button>
                    <button
                      data-testid="ai-report-copy"
                      onClick={copyReport}
                      className="btn-sharp inline-flex items-center gap-1 text-xs border border-slate-300 px-2 py-1.5 hover:border-slate-900"
                      title="Salin"
                    >
                      {copied ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      data-testid="ai-report-download"
                      onClick={downloadReport}
                      className="btn-sharp inline-flex items-center gap-1 text-xs border border-slate-300 px-2 py-1.5 hover:border-slate-900"
                      title="Unduh .md"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span className="mono text-[10px]">MD</span>
                    </button>
                    <button
                      data-testid="ai-report-docx"
                      onClick={downloadDocx}
                      className="btn-sharp inline-flex items-center gap-1 text-xs border border-blue-300 bg-blue-50 text-blue-700 px-2 py-1.5 hover:bg-blue-100"
                      title="Unduh .docx (Microsoft Word)"
                    >
                      <FileType2 className="w-3.5 h-3.5" />
                      <span className="mono text-[10px]">DOCX</span>
                    </button>
                  </>
                )}
                {aiBusy ? (
                  <button
                    data-testid="ai-report-stop"
                    onClick={stopReport}
                    className="btn-sharp inline-flex items-center gap-1.5 bg-rose-600 text-white text-xs font-semibold px-3 py-2 hover:bg-rose-700"
                  >
                    <StopCircle className="w-3.5 h-3.5" /> Berhenti
                  </button>
                ) : (
                  <button
                    data-testid="ai-report-generate"
                    onClick={generateReport}
                    disabled={photos.length === 0}
                    className="btn-sharp inline-flex items-center gap-1.5 bg-gradient-to-r from-[#0047FF] to-slate-900 text-white text-xs font-semibold px-3 py-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {aiReport ? "Regenerate" : "Generate Laporan"}
                  </button>
                )}
              </div>
            </div>

            {photos.length === 0 ? (
              <div className="text-xs text-slate-500 p-4 bg-amber-50 border border-amber-200">
                💡 Unggah minimal 1 foto dokumentasi terlebih dahulu — AI akan menganalisis
                foto untuk menyusun draft laporan otomatis.
              </div>
            ) : !aiReport && !aiBusy ? (
              <div
                className="text-xs text-slate-600 p-4 bg-slate-50 border border-slate-200 cursor-pointer hover:border-[#0047FF]"
                onClick={generateReport}
                data-testid="ai-report-cta"
              >
                Klik <b>Generate Laporan</b> — AI akan membaca {photos.length} foto SPD dan
                menyusun draft laporan terstruktur (aktivitas, temuan, rekomendasi).
                Hasil bisa Anda salin/edit sesuai kebutuhan.
              </div>
            ) : (
              <div
                data-testid="ai-report-output"
                className="bg-slate-50 border border-slate-200 p-4 max-h-96 overflow-y-auto"
              >
                {aiBusy && !aiReport && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Menganalisis {photos.length} foto dengan Claude Vision...
                  </div>
                )}
                {aiReport && <Markdown text={aiReport} />}
                {aiBusy && aiReport && (
                  <div className="mt-2 mono text-[11px] text-[#0047FF] flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    mengetik...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Report history */}
          {reports.length > 0 && (
            <div className="border-t border-slate-200 pt-6" data-testid="reports-history">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-slate-500" />
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                    Riwayat Laporan AI
                  </div>
                  <h4 className="font-display font-bold text-sm">
                    {reports.length} laporan tersimpan
                  </h4>
                </div>
              </div>
              <div className="space-y-2">
                {reports.map((r) => {
                  const isOpen = expandedReportId === r.id;
                  const preview_ = (r.content || "").split("\n").find((l) => l.trim());
                  return (
                    <div
                      key={r.id}
                      data-testid={`report-item-${r.id}`}
                      className="border border-slate-200 bg-white"
                    >
                      <div className="flex items-center gap-3 p-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-[#0047FF]/10 to-slate-100 flex items-center justify-center shrink-0">
                          <Sparkles className="w-4 h-4 text-[#0047FF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {(preview_ || "Laporan").replace(/^#+\s*/, "")}
                          </div>
                          <div className="mono text-[11px] text-slate-500">
                            {new Date(r.created_at).toLocaleString("id-ID", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            {" · "}
                            {r.photo_count || 0} foto{" · "}
                            {r.content?.length || 0} karakter
                          </div>
                        </div>
                        <button
                          data-testid={`report-toggle-${r.id}`}
                          onClick={() => setExpandedReportId(isOpen ? null : r.id)}
                          className="p-2 border border-slate-300 hover:border-slate-900"
                          title={isOpen ? "Tutup" : "Buka"}
                        >
                          {isOpen ? (
                            <ChevronUp className="w-4 h-4 text-slate-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-600" />
                          )}
                        </button>
                        <button
                          data-testid={`report-restore-${r.id}`}
                          onClick={() => {
                            setAiReport(r.content);
                            setSaved(true);
                            setExpandedReportId(null);
                            toast.success("Laporan dimuat ke panel AI di atas");
                            document
                              .querySelector('[data-testid="ai-report-section"]')
                              ?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          className="p-2 border border-slate-300 hover:border-[#0047FF] hover:text-[#0047FF]"
                          title="Muat ulang ke panel AI"
                        >
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                        <button
                          data-testid={`report-delete-${r.id}`}
                          onClick={() => {
                            if (window.confirm("Hapus laporan ini dari riwayat?"))
                              deleteReportMut.mutate(r.id);
                          }}
                          className="p-2 border border-slate-300 hover:border-rose-500 hover:text-rose-500"
                          title="Hapus"
                        >
                          <Trash2 className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                      {isOpen && (
                        <div
                          className="border-t border-slate-200 p-4 bg-slate-50 max-h-72 overflow-y-auto"
                          data-testid={`report-body-${r.id}`}
                        >
                          <Markdown text={r.content} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}


        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 flex items-center justify-between">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
            ID: {record.id.slice(0, 8)}…
          </div>
          <button
            onClick={onClose}
            className="btn-sharp px-4 py-1.5 border border-slate-300 text-xs font-semibold hover:border-slate-500"
          >
            Tutup
          </button>
        </div>
      </div>

      {/* Photo preview lightbox */}
      {preview && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setPreview(null)}
        >
          <img
            src={preview}
            alt="preview"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      {/* PDF inline preview */}
      {pdfPreview && (
        <PdfPreviewModal
          attachment={pdfPreview}
          url={attachmentUrl(pdfPreview.storage_path)}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200">
      <Icon className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mono">
          {label}
        </div>
        <div className="text-sm text-slate-800 break-words">
          {value || <span className="text-slate-300">—</span>}
        </div>
      </div>
    </div>
  );
}
